import { z } from "zod";

const hoverErrorTypeValues = [
  "HOVER_TOKEN_NOT_FOUND",
  "HOVER_TOKEN_MISSING_ADMIN_USER_ID",
  "HOVER_TOKEN_MISSING_ADMIN_USER_EMAIL",
  "HOVER_ASSIGNMENT_RESPONSE_UNEXPECTED",
  "HOVER_INVALID_EXTERNAL_SERVICE_TYPE",
  "HOVER_FILE_SYNC_EXHAUSTED",
  "HOVER_FILE_SYNC_STILL_GENERATING",
  "HOVER_ESX_UNAVAILABLE",
  "HOVER_STUCK_FILE_SYNC_WATCHDOG_FAILED",
  "HOVER_ARTIFACT_UNEXPECTED_STATUS",
] as const;

const hoverErrorTypeSchema = z.enum(hoverErrorTypeValues);
const HoverErrorType = hoverErrorTypeSchema.enum;
type HoverErrorType = z.infer<typeof hoverErrorTypeSchema>;

type ErrorDetails = {
  sentryDescription: string;
};

export type HoverErrors =
  | {
      errorType: typeof HoverErrorType.HOVER_TOKEN_NOT_FOUND;
      data: undefined;
    }
  | {
      errorType: typeof HoverErrorType.HOVER_TOKEN_MISSING_ADMIN_USER_ID;
      data: undefined;
    }
  | {
      errorType: typeof HoverErrorType.HOVER_TOKEN_MISSING_ADMIN_USER_EMAIL;
      data: undefined;
    }
  | {
      errorType: typeof HoverErrorType.HOVER_ASSIGNMENT_RESPONSE_UNEXPECTED;
      data:
        | {
            reason: "schema-mismatch";
            zodIssues: { path: string[]; code: string }[];
            receivedShapeKeys: string[] | null;
          }
        | {
            reason: "field-mismatch";
            jobIdMatches: boolean;
            emailMatches: boolean;
            orgIdMatches: boolean;
          };
    }
  | {
      errorType: typeof HoverErrorType.HOVER_INVALID_EXTERNAL_SERVICE_TYPE;
      data: undefined;
    }
  | {
      errorType: typeof HoverErrorType.HOVER_FILE_SYNC_EXHAUSTED;
      data: {
        hoverJobId: number;
        workSiteId: number;
        attemptCount: number;
        lastErrorStatus?: number;
      };
    }
  | {
      errorType: typeof HoverErrorType.HOVER_FILE_SYNC_STILL_GENERATING;
      data: {
        hoverJobId: number;
        workSiteId: number;
        attemptCount: number;
      };
    }
  | {
      errorType: typeof HoverErrorType.HOVER_ESX_UNAVAILABLE;
      data: {
        hoverJobId: number;
        workSiteId: number;
        attemptCount: number;
        lastErrorStatus?: number;
      };
    }
  | {
      errorType: typeof HoverErrorType.HOVER_STUCK_FILE_SYNC_WATCHDOG_FAILED;
      data: {
        runId: string;
        originalErrorName: string;
        originalErrorMessage: string;
      };
    }
  | {
      errorType: typeof HoverErrorType.HOVER_ARTIFACT_UNEXPECTED_STATUS;
      data: {
        artifact: "esx" | "measurement-pdf" | "cad-xml" | "full-json";
        modelId: number;
        status: number;
      };
    };

export const hoverErrorDetails = {
  [HoverErrorType.HOVER_TOKEN_NOT_FOUND]: {
    sentryDescription:
      "Hover needs to be reauthenticated, no DB token was found. This can be fixed through the remi admin tool",
  },
  [HoverErrorType.HOVER_TOKEN_MISSING_ADMIN_USER_ID]: {
    sentryDescription:
      "Hover token exists but has no adminUserId (current_user_id) stored. The /api/v2/capture_requests forjobinvite endpoint requires admin identity; reauthorize Hover or run the backfill script to refresh the token.",
  },
  [HoverErrorType.HOVER_TOKEN_MISSING_ADMIN_USER_EMAIL]: {
    sentryDescription:
      "Hover token exists but has no adminUserEmail (current_user_email) stored. The new /api/v2/capture_requests forjobinvite endpoint requires both id and email; reauthorize Hover or run the backfill script to refresh the token.",
  },
  [HoverErrorType.HOVER_ASSIGNMENT_RESPONSE_UNEXPECTED]: {
    sentryDescription:
      "Hover returned a 201 from /api/v2/capture_requests but the response didn't match the assignment shape (kind, jobId, email, or org binding). Schema drift suspected; investigate before next deploy.",
  },
  [HoverErrorType.HOVER_INVALID_EXTERNAL_SERVICE_TYPE]: {
    sentryDescription: "Invalid external service type",
  },
  [HoverErrorType.HOVER_FILE_SYNC_EXHAUSTED]: {
    sentryDescription: "Hover file sync retries exhausted after 5 attempts (4 retries)",
  },
  [HoverErrorType.HOVER_FILE_SYNC_STILL_GENERATING]: {
    sentryDescription:
      "Hover artifact was still generating (202) after all sync retries. Not a failure: the stuck-file-sync watchdog re-fires the job hourly and it syncs once Hover finishes generating. Non-paging.",
  },
  [HoverErrorType.HOVER_ESX_UNAVAILABLE]: {
    sentryDescription:
      "Hover's per-model ESX (Xactimate) export was still unavailable (403) after all sync retries. ESX is generated lazily; usually transient (the watchdog re-syncs once Hover finishes) or, rarely, a model whose ESX never generates. Non-paging — the measurement PDF is typically already synced or absent.",
  },
  [HoverErrorType.HOVER_STUCK_FILE_SYNC_WATCHDOG_FAILED]: {
    sentryDescription: "Stuck Hover file-sync watchdog cron run failed",
  },
  [HoverErrorType.HOVER_ARTIFACT_UNEXPECTED_STATUS]: {
    sentryDescription:
      "Hover v3 per-model artifact endpoint returned an unexpected 2xx status (not 200 or 202); treated as a contract violation rather than a synced artifact.",
  },
} as const satisfies Record<HoverErrorType, ErrorDetails>;
